// ============================================================
// The Select tool's clipboard: mark a region, copy it, turn it, stamp it.
//
// SELECT TAKES NOTHING AND CHANGES NOTHING. It marks a rectangle; what
// happens to it is a separate, deliberate button - which is what makes it
// safe to have a tool whose drag can span the map.
// ============================================================
import { useState, useCallback, useMemo } from 'react';
import { emptyGrid } from '../engine/battlefield/voxels';
import { copyRegion, mirrorStamp, rotateStamp, pasteStamp } from '../engine/battlefield/clipboard';

/** The terrain with a stamp pasted, its corner at `cell`. */
function pasted(current, stamp, cell, tile) {
  const out = pasteStamp(current.voxels || emptyGrid(tile), current.zones || [], stamp, cell);
  return { ...current, voxels: out.voxels, zones: out.zones };
}

/** Where the held stamp would land with its corner at the hovered cell. */
function ghostCells(stamp, hover) {
  if (!stamp || !hover) return null;
  return stamp.cells.filter((c) => c.dy === 0).map((c) => ({ x: hover.x + c.dx, z: hover.z + c.dz }));
}

const isClick = (d) => d.from.x === d.to.x && d.from.z === d.to.z;

/** Region, stamp and the verbs on them. */
export function useBuildClipboard({ terrain, setTerrain, grid, tile, tool, hover }) {
  const [region, setRegion] = useState(null);
  const [stamp, setStamp] = useState(null);
  const copySelection = useCallback(() => {
    if (region) setStamp(copyRegion(grid, terrain.zones || [], region.from, region.to));
  }, [region, grid, terrain]);
  const mirrorClipboard = useCallback((axis) => setStamp((s) => (s ? mirrorStamp(s, axis) : s)), []);
  const rotateClipboard = useCallback(() => setStamp((s) => (s ? rotateStamp(s) : s)), []);
  const clearClipboard = useCallback(() => {
    setStamp(null);
    setRegion(null);
  }, []);
  const ghost = useMemo(() => (tool === 'select' ? ghostCells(stamp, hover) : null), [tool, stamp, hover]);
  const { stampAt, selectStroke } = useStamping({ stamp, setRegion, setTerrain, tile });
  return {
    region,
    stamp,
    ghost,
    copySelection,
    stampAt,
    mirrorClipboard,
    rotateClipboard,
    clearClipboard,
    selectStroke,
  };
}

/**
 * Putting the held stamp down. `selectStroke` is what a finished Select
 * drag does: a CLICK stamps what is held; a DRAG always marks a region,
 * even while holding one - anything else leaves no way to select
 * something new without dropping the clipboard first, and makes every
 * stray drag a building.
 */
function useStamping({ stamp, setRegion, setTerrain, tile }) {
  // Through the updater form, or two stamps in quick succession both
  // build on the terrain as it was before the first.
  const stampAt = useCallback(
    (cell) => {
      if (stamp && cell) setTerrain((current) => pasted(current, stamp, cell, tile));
    },
    [stamp, setTerrain, tile],
  );
  const selectStroke = useCallback(
    (d) => {
      // A right-drag is erase everywhere else and must not be a paste
      // here - that was a real way to stamp a building by accident.
      if (d.erasing) return;
      if (isClick(d) && stamp) stampAt(d.from);
      else setRegion({ from: d.from, to: d.to });
    },
    [stamp, stampAt, setRegion],
  );
  return { stampAt, selectStroke };
}
